import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { StudentImportZone } from '@/features/institution/components/StudentImportZone'
import { StudentTable } from '@/features/institution/components/StudentTable'
import { ClassManager } from '@/features/institution/components/ClassManager'
import { SharedCourseManager } from '@/features/institution/components/SharedCourseManager'
import { ProgressReportPanel } from '@/features/institution/components/ProgressReportPanel'
import { useInstitution } from '@/features/institution/hooks/useInstitution'

const TABS = ['students', 'classes', 'courses', 'report'] as const

type Tab = (typeof TABS)[number]

export function InstitutionPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('students')
  const { data: institution, isLoading, isError } = useInstitution()

  return (
    <div className="min-h-full overflow-y-auto bg-surface px-container-px-mobile pb-20 pt-6 md:px-container-px-desktop md:pb-8">
      <div className="mx-auto flex max-w-4xl flex-col gap-6">
        <header>
          <h1 className="font-display text-2xl font-bold text-on-surface">
            {institution?.name ?? t('institution.page_title')}
          </h1>
          {institution && (
            <p className="mt-1 text-sm text-on-surface-variant">
              {t('institution.header_counts', {
                students: institution.students_count,
                classes: institution.classes_count,
              })}
            </p>
          )}
        </header>

        {isLoading && <div className="h-40 animate-pulse rounded-2xl bg-surface-container" />}

        {isError && <Alert variant="error">{t('institution.no_access')}</Alert>}

        {institution && (
          <>
            <div role="tablist" aria-label={t('institution.page_title')} className="flex flex-wrap gap-2">
              {TABS.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  id={`institution-tab-${value}`}
                  aria-selected={tab === value}
                  aria-controls="institution-panel"
                  onClick={() => setTab(value)}
                  className={[
                    'min-h-11 rounded-full px-4 text-sm font-semibold transition-colors',
                    tab === value
                      ? 'bg-primary text-white'
                      : 'bg-surface-low text-on-surface-variant hover:bg-surface-container',
                  ].join(' ')}
                >
                  {t(`institution.tabs.${value}`)}
                </button>
              ))}
            </div>

            <div
              role="tabpanel"
              id="institution-panel"
              aria-labelledby={`institution-tab-${tab}`}
              className="flex flex-col gap-8"
            >
              {tab === 'students' && (
                <>
                  <StudentImportZone />
                  <StudentTable />
                </>
              )}
              {tab === 'classes' && <ClassManager />}
              {tab === 'courses' && <SharedCourseManager />}
              {tab === 'report' && <ProgressReportPanel />}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
